import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  CustomerAccountsAdBlocksFlatTeamSettingGroupById,
  type CustomerAccountsAdBlocksFlatTeamSettingGroupId,
} from "./groups";

type CustomerAccountsAdBlocksSettingInput = SettingRegistryMetadataInput;

function setting(
  metadata: CustomerAccountsAdBlocksSettingInput,
): CustomerAccountsAdBlocksSettingInput {
  return metadata;
}

function customerAccountsAdBlocksGroup<
  const Group extends CustomerAccountsAdBlocksFlatTeamSettingGroupId,
  const T extends Record<string, CustomerAccountsAdBlocksSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

// Keys must be real flat schema keys from ./settings.ts (wired into
// FlatTeamSettingsSchema). Each equals the team.settings.* path, dots→slashes.
const CustomerAccountsAdBlocksRegistry = {
  ...customerAccountsAdBlocksGroup("customer_accounts_ad_blocks.ad_blocks", {
    "customerAccounts/adBlocks": setting({
      label: "Ad blocks",
      description:
        "Promotional ad blocks shown in the storefront customer account, edited through the ad block builder and published one at a time.",
      synonyms: [
        "ad blocks",
        "ad block",
        "customer account ad blocks",
        "promo blocks",
      ],
    }),
  }),
};

type CustomerAccountsAdBlocksFlatTeamSettingKey =
  keyof typeof CustomerAccountsAdBlocksRegistry;
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const CustomerAccountsAdBlocksFlatTeamSettingKeys = Object.keys(
  CustomerAccountsAdBlocksRegistry,
) as CustomerAccountsAdBlocksFlatTeamSettingKey[];

export const CustomerAccountsAdBlocksFlatTeamSettingCatalogDefinitions =
  CustomerAccountsAdBlocksFlatTeamSettingKeys.map((key) => {
    const definition = CustomerAccountsAdBlocksRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group:
          CustomerAccountsAdBlocksFlatTeamSettingGroupById[definition.group],
      }),
      valueSource: { adapter: "teamSettings", key },
    };
  }) satisfies readonly SettingDefinition<
    CustomerAccountsAdBlocksFlatTeamSettingKey,
    CustomerAccountsAdBlocksFlatTeamSettingGroupId,
    TeamSettingsSettingValueSource<CustomerAccountsAdBlocksFlatTeamSettingKey>
  >[];

export const CustomerAccountsAdBlocksFlatTeamSettingMetadataByKey =
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  Object.fromEntries(
    CustomerAccountsAdBlocksFlatTeamSettingCatalogDefinitions.map(
      (definition) => [definition.id, definition.metadata],
    ),
  ) as Record<CustomerAccountsAdBlocksFlatTeamSettingKey, SettingMetadata>;
